import React, { useState, useEffect, useRef } from 'react';
import './ImageScroller.css';

const ImageScroller = ({ images, title, autoScrollInterval = 5000 }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const scrollerRef = useRef(null);
    const autoScrollTimerRef = useRef(null);

    // Mouse drag state
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Use Intersection Observer to update the active index based on visible image
    useEffect(() => {
        if (!scrollerRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const index = parseInt(entry.target.dataset.index, 10);
                        setActiveIndex(index);
                    }
                });
            },
            { threshold: 0.6, root: null }
        );

        const sections = scrollerRef.current.querySelectorAll('.fullscreen-image-section');
        sections.forEach(section => observer.observe(section));

        return () => {
            sections.forEach(section => observer.unobserve(section));
        };
    }, [images]);

    // Auto-scroll functionality
    useEffect(() => {
        if (!scrollerRef.current || isPaused || isDragging) return;

        const startAutoScroll = () => {
            autoScrollTimerRef.current = setInterval(() => {
                const nextIndex = (activeIndex + 1) % images.length;
                scrollToImage(nextIndex);
            }, autoScrollInterval);
        };

        if (autoScrollTimerRef.current) {
            clearInterval(autoScrollTimerRef.current);
        }
        startAutoScroll();

        return () => {
            if (autoScrollTimerRef.current) {
                clearInterval(autoScrollTimerRef.current);
            }
        };
    }, [activeIndex, images.length, isPaused, autoScrollInterval, isDragging]);

    // Scroll to a specific image when indicator is clicked
    const scrollToImage = (index) => {
        if (scrollerRef.current) {
            const targetPosition = index * window.innerWidth;
            scrollerRef.current.scrollTo({
                left: targetPosition,
                behavior: 'smooth'
            });
        }
    };

    // Navigate to previous image
    const navigatePrev = () => {
        const prevIndex = (activeIndex - 1 + images.length) % images.length;
        scrollToImage(prevIndex);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
    };
    const handleTouchStart = (e) => {
        if (!scrollerRef.current) return;
        setIsDragging(true);
        setIsPaused(true);
        setStartX(e.touches[0].pageX - scrollerRef.current.offsetLeft);
        setScrollLeft(scrollerRef.current.scrollLeft);
    };

    const handleTouchMove = (e) => {
        if (!isDragging || !scrollerRef.current) return;
        const x = e.touches[0].pageX - scrollerRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Adjust scrolling speed
        scrollerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = () => {
        setIsDragging(false);

        // Snap to nearest slide after dragging
        if (scrollerRef.current) {
            const slideWidth = window.innerWidth;
            const currentPosition = scrollerRef.current.scrollLeft;
            const targetIndex = Math.round(currentPosition / slideWidth);

            setTimeout(() => {
                scrollToImage(targetIndex);
            }, 0);
        }

        setTimeout(() => setIsPaused(false), 5000);
    };
    // Navigate to next image
    const navigateNext = () => {
        const nextIndex = (activeIndex + 1) % images.length;
        scrollToImage(nextIndex);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
    };

    // Mouse drag handlers
    const handleMouseDown = (e) => {
        if (!scrollerRef.current) return;
        setIsDragging(true);
        setIsPaused(true);
        setStartX(e.pageX - scrollerRef.current.offsetLeft);
        setScrollLeft(scrollerRef.current.scrollLeft);
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !scrollerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollerRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Adjust scrolling speed
        scrollerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setTimeout(() => setIsPaused(false), 5000);
    };

    return (
        <div className="image-scroller-wrapper">
            {/* Previous button */}
            <button
                className="nav-arrow prev-arrow"
                onClick={navigatePrev}
                aria-label="Previous image"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                </svg>
            </button>

            {/* Scrollable container */}
            <div
                className="fullscreen-scroller-container"
                ref={scrollerRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                {images.map((image, index) => (
                    <section
                        key={index}
                        className="fullscreen-image-section"
                        data-index={index}
                    >
                        <div className="fullscreen-image-wrapper">
                            <img
                                src={image.src}
                                alt={image.alt || `Image ${index + 1}`}
                                className="fullscreen-image"
                                draggable={false} // Prevent native dragging
                            />
                            <div className="image-caption-overlay">
                                <h3>{image.alt || `Image ${index + 1}`}</h3>
                                {image.caption && <p>{image.caption}</p>}
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            {/* Next button */}
            <button
                className="nav-arrow next-arrow"
                onClick={navigateNext}
                aria-label="Next image"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                </svg>
            </button>

            {/* Navigation indicators placed outside the scrollable container */}
            <div className="image-indicators">
                {images.map((_, index) => (
                    <button
                        key={index}
                        className={`indicator ${activeIndex === index ? 'active' : ''}`}
                        onClick={() => scrollToImage(index)}
                        aria-label={`Go to image ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ImageScroller;